import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Download, TrendingUp, BarChart3, Clock, RefreshCw } from 'lucide-react';
import { useExecutions } from '../../hooks/useExecutions';
import { DataTable } from '../../components/ui/data-table';
import { columns, Execution } from './columns';

export function History() {
  const [pageSize] = useState(20);
  const [currentPage] = useState(0);

  // Fetch real execution data from API
  const { data: executionsData, isLoading } = useExecutions(pageSize, currentPage * pageSize);

  const executions = executionsData?.items || [];
  const totalExecutions = executionsData?.total || 0;

  // Transform data for DataTable
  const tableData: Execution[] = executions.map(execution => ({
    execution_id: execution.execution_id || '',
    workflow_id: execution.workflow_id,
    status: execution.status,
    steps_completed: execution.steps_completed,
    steps_total: execution.steps_total,
    started_at: execution.started_at,
    completed_at: execution.completed_at,
    duration: execution.duration,
  }));

  // Calculate enhanced statistics
  const stats = {
    totalExecutions: totalExecutions,
    completedExecutions: executions.filter(e => e.status === 'completed').length,
    runningExecutions: executions.filter(e => e.status === 'running').length,
    failedExecutions: executions.filter(e => e.status === 'failed').length,
    averageExecutionTime: executions.length > 0
      ? Math.round(executions.reduce((sum, e) => {
          if (e.started_at && e.completed_at) {
            return sum + (new Date(e.completed_at).getTime() - new Date(e.started_at).getTime()) / 1000;
          }
          return sum;
        }, 0) / executions.filter(e => e.completed_at).length)
      : 0,
    successRate: executions.length > 0
      ? Math.round((executions.filter(e => e.status === 'completed').length / executions.length) * 100)
      : 0,
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return minutes > 0 ? `${minutes}m ${remainingSeconds}s` : `${remainingSeconds}s`;
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="md:flex md:items-center md:justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
              Execution History
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Loading execution history...
            </p>
          </div>
        </div>
        
        {/* Enhanced Statistics Cards */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="shadow-lg">
              <CardContent className="p-6">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-1/2 mb-1"></div>
                  <div className="h-3 bg-gray-200 rounded w-full"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Execution History
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            View and manage your workflow execution history with real-time insights
          </p>
        </div>
        <div className="mt-4 flex space-x-3 md:mt-0 md:ml-4">
          <Button variant="outline" className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 hover:bg-green-100 transition-all duration-300">
            <Download className="h-4 w-4 mr-2" />
            Export History
          </Button>
        </div>
      </div>

      {/* Enhanced Statistics Cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-700">Total Executions</p>
                <p className="text-3xl font-bold text-blue-800">{stats.totalExecutions}</p>
                <p className="text-sm text-blue-600">All time</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <BarChart3 className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-700">Success Rate</p>
                <p className="text-3xl font-bold text-green-800">{stats.successRate}%</p>
                <p className="text-sm text-green-600">Recent performance</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-amber-700">Avg Duration</p>
                <p className="text-3xl font-bold text-amber-800">{formatDuration(stats.averageExecutionTime)}</p>
                <p className="text-sm text-amber-600">Per execution</p>
              </div>
              <div className="p-3 bg-amber-100 rounded-lg">
                <Clock className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-700">Active Now</p>
                <p className="text-3xl font-bold text-purple-800">{stats.runningExecutions}</p>
                <p className="text-sm text-purple-600">Running executions</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <RefreshCw className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* DataTable */}
      <Card>
        <CardHeader>
          <CardTitle>Execution History</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              <div className="animate-pulse">
                <div className="h-10 bg-gray-200 rounded mb-4"></div>
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="h-12 bg-gray-200 rounded"></div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={tableData}
              searchPlaceholder="Search by workflow name..."
              searchColumn="workflow_id"
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}